import { useState, useRef, useEffect, useCallback } from "react";

const API = import.meta.env.VITE_API_URL || "https://vitalcasa-crm-production.up.railway.app/api";
import { useTheme } from "../components/ThemeProvider";
import { dialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";
import AnaImg from "../assets/Ana_definitiva_con_fondo-removebg.png";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
function horaActual() {
  return new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────────────────────────────────────────
// CARRUSEL DE FOTOS
// ─────────────────────────────────────────────────────────────
function CarruselFotos({ fotos, isDark, autoplay = false, onFin }) {
  const [idx, setIdx] = useState(0);
  const [capas, setCapas] = useState({ a: 0, b: null, activa: "a", animando: false });
  const total = fotos?.length || 0;
  const finLlamado = useRef(false);

  useEffect(() => {
    setIdx(0);
    setCapas({ a: 0, b: null, activa: "a", animando: false });
    finLlamado.current = false;
  }, [fotos]);

  const urlFoto = (i) => {
    if (i === null || i === undefined || !fotos?.[i]) return "";
    const f = fotos[i];
    return f.startsWith("http") ? f : `https://vitalcasa-crm-production.up.railway.app${f}`;
  };

  useEffect(() => {
    if (!autoplay || total === 0) return;
    const DURACION = 5000;
    const timer = setTimeout(() => {
      if (idx < total - 1) {
        const siguiente = idx + 1;
        const capaEntrante = capas.activa === "a" ? "b" : "a";
        setCapas(prev => ({ ...prev, [capaEntrante]: siguiente, animando: true }));
        setTimeout(() => {
          setCapas(prev => ({ ...prev, activa: capaEntrante, animando: false }));
          setIdx(siguiente);
        }, 800);
      } else {
        if (!finLlamado.current) { finLlamado.current = true; if (onFin) onFin(); }
      }
    }, DURACION);
    return () => clearTimeout(timer);
  }, [idx, autoplay, total, onFin]);

  if (total === 0) return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: isDark ? "#0d1117" : "#e2e8f0" }}>
      <span style={{ fontSize: 13, color: "#6b7280" }}>Sin fotos disponibles</span>
    </div>
  );

  const estiloFoto = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "opacity 0.8s ease" };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#000", overflow: "hidden" }}>
      <img src={urlFoto(capas.a)} style={{ ...estiloFoto, opacity: capas.activa === "a" ? 1 : 0 }} alt="" />
      {capas.b !== null && <img src={urlFoto(capas.b)} style={{ ...estiloFoto, opacity: capas.activa === "b" ? 1 : 0 }} alt="" />}
      {total > 1 && (
        <div style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.15)", padding: "3px 10px", borderRadius: 20, fontSize: 11, color: "rgba(255,255,255,0.8)", fontWeight: 600, zIndex: 5 }}>
          {idx + 1} / {total}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BURBUJA DE CHAT (estilo WhatsApp)
// ─────────────────────────────────────────────────────────────
function Burbuja({ msg, isDark }) {
  const esAna = msg.rol === "ana";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: esAna ? "flex-start" : "flex-end", marginBottom: 6 }}>
      <div style={{
        maxWidth: "85%", padding: "9px 12px",
        borderRadius: esAna ? "4px 14px 14px 14px" : "14px 4px 14px 14px",
        background: esAna
          ? (isDark ? "rgba(37,99,235,0.18)" : "rgba(37,99,235,0.12)")
          : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"),
        border: esAna ? "1px solid rgba(37,99,235,0.3)" : `1px solid ${isDark ? "#21262d" : "#e2e8f0"}`,
      }}>
        <div style={{ fontSize: 13, color: isDark ? "#e2e8f0" : "#1e293b", lineHeight: 1.5 }}>{msg.texto}</div>
        <div style={{ fontSize: 10, color: esAna ? "#3b82f6" : "#6b7280", marginTop: 3, textAlign: esAna ? "left" : "right" }}>{msg.hora}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BOTÓN DE CONTROL
// ─────────────────────────────────────────────────────────────
function CtrlBtn({ activo, color = "green", onClick, title, children, disabled = false }) {
  const colores = {
    green: { border: "#22c55e", bg: "rgba(34,197,94,0.15)", text: "#22c55e" },
    orange: { border: "#f59e0b", bg: "rgba(245,158,11,0.15)", text: "#f59e0b" },
  };
  const c = colores[color];
  return (
    <button onClick={onClick} title={title} disabled={disabled} style={{
      width: 34, height: 34, borderRadius: 8,
      border: `1px solid ${activo ? c.border : "#21262d"}`,
      background: activo ? c.bg : "transparent",
      color: activo ? c.text : "#6b7280",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.2s", opacity: disabled ? 0.35 : 1,
      position: "relative", flexShrink: 0,
    }}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// FRANJA DATOS PROPIEDAD (plantilla 3)
// ─────────────────────────────────────────────────────────────
function FranjaDatos({ propiedad, isDark, border, textPrimary, textMuted, anaHablando, onCerrar }) {
  if (!propiedad) return null;
  const precio = propiedad.precio ? Number(propiedad.precio).toLocaleString("es-ES") + " €" : "A consultar";
  const stats = [
    propiedad.habitaciones && { icon: "🛏", val: propiedad.habitaciones, label: "Hab." },
    propiedad.banos && { icon: "🚿", val: propiedad.banos, label: "Baños" },
    propiedad.m2_construidos && { icon: "📐", val: propiedad.m2_construidos, label: "m²" },
    propiedad.m2_terrazas && { icon: "🌿", val: propiedad.m2_terrazas, label: "terraza" },
  ].filter(Boolean);
  const extras = [
    propiedad.eq_piscina_priv && "Piscina privada",
    propiedad.eq_piscina_comun && "Piscina común",
    propiedad.eq_garaje && "Garaje",
    propiedad.eq_aire && "Aire acond.",
    propiedad.ub_primera_linea && "1ª línea playa",
  ].filter(Boolean);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      padding: "0 16px",
      background: isDark ? "rgba(13,17,23,0.97)" : "rgba(248,250,252,0.97)",
      borderTop: `1px solid ${border}`,
      flex: "0 0 auto", height: "100%",
    }}>
      {/* Hueco para el avatar flotante de Ana (60px + 16px margen izq + 14px gap) */}
      <div style={{ width: 60, flexShrink: 0 }} />

      {/* Datos */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 1 }}>
          {propiedad.referencia} · {propiedad.tipo_inmueble || "Inmueble"} · {propiedad.poblacion || ""}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 4 }}>
          {propiedad.web_titulo || propiedad.titulo || "Sin título"}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {stats.map(s => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <span style={{ fontSize: 12 }}>{s.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: textPrimary }}>{s.val}</span>
              <span style={{ fontSize: 11, color: textMuted }}>{s.label}</span>
            </div>
          ))}
          {extras.slice(0, 3).map(e => (
            <span key={e} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 20, background: isDark ? "rgba(37,99,235,0.12)" : "rgba(37,99,235,0.08)", color: "#2563eb", border: "1px solid rgba(37,99,235,0.2)", fontWeight: 600 }}>{e}</span>
          ))}
        </div>
      </div>

      {/* Precio + cerrar */}
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontSize: 10, color: textMuted, marginBottom: 1 }}>Precio</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#2563eb", marginBottom: 8 }}>{precio}</div>
        <button onClick={onCerrar} style={{
          fontSize: 11, padding: "3px 10px", borderRadius: 6,
          background: "transparent", border: `1px solid ${border}`,
          color: textMuted, cursor: "pointer",
        }}>✕ Cerrar</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────
export default function Ana() {
  const { theme } = useTheme();
  const isDark = theme.name === "dark";
  const cfgGuardada = (() => { try { return JSON.parse(localStorage.getItem("ana_cfg") || "{}"); } catch { return {}; } })();

  // ── Permisos granulares ──
  const { puede } = usePermiso();
  const puedeVerVoz = puede("ana_voz", "ver");
  const puedeVerTexto = puede("ana_texto", "ver");
  const puedeVerMicrofono = puede("ana_microfono", "ver");
  const puedeVerTeclado = puede("ana_teclado", "ver");
  const puedeVerNuevaSesion = puede("ana_nueva_sesion", "ver");
  const puedeVerPrompts = puede("ana_prompts", "ver");
  const puedeVerDebug = puede("ana_debug", "ver");

  // ── Colores ──
  const bgPage = theme.colors.bgApp;
  const bgPanel = theme.colors.bgPanel;
  const border = theme.colors.border;
  const textPrimary = theme.colors.textPrimary;
  const textMuted = theme.colors.textMuted;

  // ── Estados ──
  const [mensajes, setMensajes] = useState([{ id: 1, rol: "ana", texto: "¡Hola! Soy Ana, tu asesora virtual. ¿En qué puedo ayudarte?", hora: horaActual() }]);
  const [inputTexto, setInputTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [anaHablando, setAnaHablando] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [debugPanel, setDebugPanel] = useState(false);
  const [debugHistorial, setDebugHistorial] = useState([]);
  const [subtituloActual, setSubtituloActual] = useState("");

  // Configuración de modos
  const [vozActiva, setVozActiva] = useState(cfgGuardada.voz ?? false);
  const [textoActivo, setTextoActivo] = useState(cfgGuardada.texto ?? true);
  const [microClienteActivo, setMicroClienteActivo] = useState(false);
  const [textoClienteActivo, setTextoClienteActivo] = useState(cfgGuardada.textoCliente ?? true);
  const [transcribiendo, setTranscribiendo] = useState(false);

  // Propiedades
  const [propiedadActiva, setPropiedadActiva] = useState(null);
  const [panelInmersivo, setPanelInmersivo] = useState(false);
  const [transicionando, setTransicionando] = useState(false);

  // ── Refs ──
  const audioRef = useRef(null);
  const colaProps = useRef([]);
  const timerLimpiezaRef = useRef(null);
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const enviarTextoVozRef = useRef(null);
  const microContinuoRef = useRef(false);
  const enviandoRef = useRef(false); // lock anti-doble-envío

  // ── Plantilla que el usuario ha forzado manualmente (null = automático) ──
  // null = el modo lo decide el sistema automáticamente
  // "P1" / "P2" / "P3" = el usuario lo ha fijado con los botones
  const [plantillaForzada, setPlantillaForzada] = useState(null);

  // ── MODO actual ──
  // Si el usuario ha forzado una plantilla, se respeta siempre.
  // Si no, el sistema elige automáticamente:
  // P3 = propiedades activas
  // P4 = voz activa + texto desactivado (Ana sola, pantalla completa)
  // P2 = voz activa + texto activo (Ana + cliente + conversación)
  // P1 = voz desactivada (solo chat central)
  const modoAuto = panelInmersivo ? "P3" : vozActiva && !textoActivo ? "P4" : vozActiva ? "P2" : "P1";
  const modo = plantillaForzada ?? modoAuto;

  // ── Guardar configuración ──
  useEffect(() => {
    localStorage.setItem("ana_cfg", JSON.stringify({ voz: vozActiva, texto: textoActivo, textoCliente: textoClienteActivo }));
  }, [vozActiva, textoActivo, textoClienteActivo]);

  // ── Scroll automático del chat ──
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [mensajes, cargando, subtituloActual]);

  // ── Limpieza al desmontar ──
  useEffect(() => {
    return () => {
      microContinuoRef.current = false;
      if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
      if (audioRef.current) { clearInterval(audioRef.current._typingTimer); audioRef.current.pause(); }
    };
  }, []);

  // ── Cerrar panel de propiedades ──
  const cerrarPanelInmersivo = useCallback(() => {
    setPanelInmersivo(false);
    setPlantillaForzada(null);
    colaProps.current = []; // vaciar cola — no seguir mostrando más
    if (timerLimpiezaRef.current) clearTimeout(timerLimpiezaRef.current);
    timerLimpiezaRef.current = setTimeout(() => {
      timerLimpiezaRef.current = null;
      setPropiedadActiva(null);
    }, 800);
    // Notificar a Ana que el cliente ha cerrado las imágenes
    if (enviarTextoVozRef.current) {
      setTimeout(() => enviarTextoVozRef.current("__cerrar_panel__"), 900);
    }
  }, []);

  // ── TTS ──
  const reproducirVoz = useCallback(async (texto, onFin) => {
    if (!vozActiva) { if (onFin) onFin(); return; }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ texto }),
      });
      if (!res.ok) { if (onFin) onFin(); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) { audioRef.current.pause(); URL.revokeObjectURL(audioRef.current.src); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => {
        setAnaHablando(true);
        setTimeout(() => {
          const durMs = (audio.duration && isFinite(audio.duration)) ? audio.duration * 1000 : 6000;
          const intervalo = Math.max(15, durMs / texto.length);
          let i = 0;
          const timer = setInterval(() => { i++; setSubtituloActual(texto.slice(0, i)); if (i >= texto.length) clearInterval(timer); }, intervalo);
          audio._typingTimer = timer;
        }, 150);
      };
      audio.onended = () => {
        clearInterval(audio._typingTimer);
        setAnaHablando(false);
        setSubtituloActual("");
        if (onFin) onFin();
        if (microContinuoRef.current) setTimeout(() => arrancarRecognition(), 400);
      };
      audio.onerror = () => { clearInterval(audio._typingTimer); setAnaHablando(false); setSubtituloActual(""); if (onFin) onFin(); };
      audio.play();
    } catch (e) { console.error("TTS error:", e); if (onFin) onFin(); }
  }, [vozActiva]);

  // ── Micrófono — modo turno ──
  const arrancarRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || !microContinuoRef.current) return;
    if (audioRef.current && !audioRef.current.paused) return;
    if (recognitionRef.current) return;
    const rec = new SR();
    rec.lang = "es-ES"; rec.continuous = false; rec.interimResults = true; rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    let textoFinal = ""; let timerSilencio = null; let iniciado = false;

    // Timeout anti-bucle: si no arranca en 1.5s, abortar
    const timerArranque = setTimeout(() => {
      if (!iniciado) {
        try { rec.stop(); } catch {}
        recognitionRef.current = null;
        if (microContinuoRef.current) setTimeout(() => arrancarRecognition(), 2000);
      }
    }, 1500);

    rec.onstart = () => { iniciado = true; clearTimeout(timerArranque); setTranscribiendo(true); };
    rec.onresult = (ev) => {
      let interino = ""; textoFinal = "";
      for (let i = 0; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) textoFinal += ev.results[i][0].transcript;
        else interino += ev.results[i][0].transcript;
      }
      setInputTexto(textoFinal + interino);
      if (timerSilencio) clearTimeout(timerSilencio);
      if (textoFinal.trim()) timerSilencio = setTimeout(() => rec.stop(), 2000);
    };
    rec.onend = () => {
      if (timerSilencio) clearTimeout(timerSilencio);
      setTranscribiendo(false); recognitionRef.current = null;
      if (textoFinal.trim() && enviarTextoVozRef.current) {
        enviarTextoVozRef.current(textoFinal.trim());
      } else if (microContinuoRef.current) {
        setTimeout(() => arrancarRecognition(), 300);
      }
    };
    rec.onerror = (ev) => {
      setTranscribiendo(false); recognitionRef.current = null;
      if (ev.error === "not-allowed") { microContinuoRef.current = false; setMicroClienteActivo(false); void dialogs.alert("Permiso de micrófono denegado."); return; }
      if (microContinuoRef.current) setTimeout(() => arrancarRecognition(), 1000);
    };
    try { rec.start(); } catch (e) { clearTimeout(timerArranque); console.error("🎤 rec.start error:", e); recognitionRef.current = null; if (microContinuoRef.current) setTimeout(() => arrancarRecognition(), 1000); }
  }, []);

  const iniciarMicrofono = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { void dialogs.alert("Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge."); return; }
    if (microContinuoRef.current) {
      microContinuoRef.current = false;
      if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
      setMicroClienteActivo(false); setTranscribiendo(false); setInputTexto("");
    } else {
      // Limpiar audio colgado que bloquearía arrancarRecognition
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      microContinuoRef.current = true; setMicroClienteActivo(true); arrancarRecognition();
    }
  }, [arrancarRecognition]);

  // ── Saludo inicial con voz ──
  useEffect(() => {
    if (!vozActiva) return;
    if (sessionStorage.getItem("ana_saludo")) return;
    sessionStorage.setItem("ana_saludo", "1");
    const saludo = mensajes[0]?.texto;
    if (!saludo) return;
    const token = localStorage.getItem("token");
    fetch(`${API}/ana/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ mensaje: "__saludo_inicial__" }),
    }).catch(() => {});
    setTimeout(() => reproducirVoz(saludo), 2000);
  }, []);

  // ── Enviar mensaje ──
  const enviarMensaje = async (textoDirecto) => {
    const texto = (typeof textoDirecto === "string" ? textoDirecto : inputTexto).trim();
    if (!texto || cargando) return;
    if (enviandoRef.current) return; // guard anti-doble-envío (React StrictMode)
    enviandoRef.current = true;

    // Mensaje interno — no mostrar en el chat, solo enviar al backend
    const esMensajeInterno = texto === "__cerrar_panel__" || texto === "__saludo_inicial__";

    if (!esMensajeInterno) {
      const msgUsuario = { id: Date.now(), rol: "usuario", texto, hora: horaActual() };
      setMensajes(prev => [...prev, msgUsuario]);
    }
    setInputTexto(""); setCargando(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/ana/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ mensaje: texto }),
      });
      const data = await res.json();
      const ok = data && (data.ok === true || data.respuesta);

      if (data.debug) {
        setDebugInfo(data.debug);
        setDebugHistorial(prev => [{
          hora: horaActual(),
          mensaje: texto.substring(0, 60) + (texto.length > 60 ? "…" : ""),
          ...data.debug
        }, ...prev].slice(0, 20));
      }

      if (ok && data.propiedades?.length > 0) {
        colaProps.current = [...data.propiedades];
        const primera = colaProps.current.shift();
        if (timerLimpiezaRef.current) { clearTimeout(timerLimpiezaRef.current); timerLimpiezaRef.current = null; }
        setPropiedadActiva(primera); setPanelInmersivo(true);
        setPlantillaForzada("P3"); // forzar plantilla fotos
      } else if (ok && data.propiedad) {
        colaProps.current = [];
        if (timerLimpiezaRef.current) { clearTimeout(timerLimpiezaRef.current); timerLimpiezaRef.current = null; }
        setPropiedadActiva(data.propiedad); setPanelInmersivo(true);
        setPlantillaForzada("P3"); // forzar plantilla fotos
      } else if (ok && !data.propiedad && !data.propiedades && panelInmersivo) {
        cerrarPanelInmersivo();
      }

      const txtRespuesta = data?.respuesta || "Lo siento, no he podido procesar tu mensaje.";
      const respAna = { id: Date.now() + 1, rol: "ana", texto: txtRespuesta, hora: horaActual() };
      if (vozActiva) {
        reproducirVoz(txtRespuesta, () => setMensajes(prev => [...prev, respAna]));
      } else {
        setMensajes(prev => [...prev, respAna]);
      }
    } catch {
      setMensajes(prev => [...prev, { id: Date.now() + 1, rol: "ana", texto: "No he podido conectar con el servidor.", hora: horaActual() }]);
    } finally { setCargando(false); enviandoRef.current = false; }
  };

  // Mantener ref actualizada
  enviarTextoVozRef.current = enviarMensaje;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarMensaje(); }
  };

  // ── Nueva sesión ──
  const nuevaSesion = async () => {
    if (!(await dialogs.confirm("¿Iniciar nueva sesión? Se borrará el historial de esta conversación."))) return;
    const token = localStorage.getItem("token");
    await fetch(`${API}/ana/nueva-sesion`, {
      method: "POST", headers: { "Authorization": `Bearer ${token}` },
    }).catch(() => {});
    sessionStorage.removeItem("ana_saludo");
    setMensajes([{ id: Date.now(), rol: "ana", texto: "¡Hola! Soy Ana, tu asesora virtual. ¿En qué puedo ayudarte?", hora: horaActual() }]);
    setDebugInfo(null);
    setDebugHistorial([]);
    setDebugPanel(false);
    // Cerrar panel de propiedades y volver a P1/P2 según voz activa
    setPropiedadActiva(null);
    setPanelInmersivo(false);
    setPlantillaForzada(null); // modo automático: P2 si voz, P1 si no
    colaProps.current = [];
    if (timerLimpiezaRef.current) { clearTimeout(timerLimpiezaRef.current); timerLimpiezaRef.current = null; }
    if (audioRef.current) { clearInterval(audioRef.current._typingTimer); audioRef.current.pause(); setAnaHablando(false); setSubtituloActual(""); }
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  // Fotos de la propiedad activa
  const fotosActivas = (() => {
    if (!propiedadActiva?.fotos) return [];
    const f = propiedadActiva.fotos;
    if (typeof f === "string") return f.replace(/^\{|\}$/g, "").split(",").map(x => x.trim()).filter(Boolean);
    return f;
  })();

  // ── CSS global con keyframes ──
  const cssGlobal = `
    @keyframes pulse { 0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.5;transform:scale(1.3);} }
    @keyframes dotBounce { 0%,80%,100%{transform:translateY(0);}40%{transform:translateY(-6px);} }
    @keyframes blink { 0%,100%{opacity:1;}50%{opacity:0;} }
    .dot-bounce { animation: dotBounce 1.2s infinite; }
    .dot-bounce:nth-child(2) { animation-delay: 0.15s; }
    .dot-bounce:nth-child(3) { animation-delay: 0.3s; }
    .blink-cursor { animation: blink 0.8s infinite; }
  `;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 57px)", background: bgPage, overflow: "hidden" }}>
      <style>{cssGlobal}</style>

      {/* ══════════════════════════════════════════════════
          HEADER ANA
      ══════════════════════════════════════════════════ */}
      <div style={{
        height: 66, minHeight: 66, flexShrink: 0,
        background: bgPanel, borderBottom: `1px solid ${border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", zIndex: 30,
      }}>
        {/* Identidad */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", overflow: "hidden", border: `2px solid ${anaHablando ? "#22c55e" : "rgba(37,99,235,0.4)"}`, transition: "border-color 0.3s", flexShrink: 0 }}>
            <img src={AnaImg} alt="Ana" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 15%" }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: textPrimary }}>Ana</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
              <div style={{ fontSize: 11, color: textMuted }}>Asistente IA · En línea</div>
              {puedeVerDebug && debugInfo && (
                <button onClick={() => setDebugPanel(p => !p)} style={{ fontSize: 10, color: "#f59e0b", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 4, padding: "1px 8px", marginTop: 2, cursor: "pointer" }}>
                  🔍 {debugInfo.total_resultados ?? "?"} props · {debugPanel ? "▲ ocultar" : "▼ ver debug"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Panel debug */}
        {puedeVerDebug && debugPanel && debugHistorial.length > 0 && (
          <div style={{
            position: "absolute", top: 66, left: 0, right: 0, zIndex: 100,
            background: isDark ? "#0d1117" : "#fff",
            border: `1px solid ${border}`, borderTop: "none",
            padding: "12px 20px", maxHeight: 340, overflowY: "auto",
            fontSize: 11, fontFamily: "monospace",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: "#f59e0b" }}>🔍 DEBUG ANA — últimos {debugHistorial.length} turnos</span>
              <button onClick={() => setDebugHistorial([])} style={{ fontSize: 10, background: "none", border: `1px solid ${border}`, borderRadius: 4, padding: "1px 6px", cursor: "pointer", color: textMuted }}>limpiar</button>
            </div>
            {debugHistorial.map((d, i) => (
              <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${border}` }}>
                <div style={{ color: "#f59e0b", marginBottom: 3 }}>
                  [{d.hora}] Turno {d.turno} — "{d.mensaje}"
                </div>
                {/* Filtros acumulados */}
                <div style={{ color: isDark ? "#93c5fd" : "#2563eb", marginBottom: 2 }}>
                  📋 Filtros: {Object.keys(d.filtros || {}).length === 0 ? "ninguno" : Object.entries(d.filtros).map(([k,v]) => `${k}=${JSON.stringify(v)}`).join(" · ")}
                </div>
                {/* Resultados */}
                <div style={{ color: d.total_resultados === 0 ? "#ef4444" : d.total_resultados <= 10 ? "#22c55e" : "#f59e0b", marginBottom: 2 }}>
                  🏠 Propiedades: {d.total_resultados ?? "?"}{d.ref_detectada ? ` · Ref mostrada: ${d.ref_detectada}` : ""}
                </div>
                {d.estrategia_busqueda && (
                  <div style={{ color: isDark ? "#5eead4" : "#0f766e", marginBottom: 2 }}>
                    🧭 Estrategia: {d.estrategia_busqueda.action}
                    {d.estrategia_busqueda.expansion?.suggestions?.length ? ` · ampliar a ${d.estrategia_busqueda.expansion.suggestions.join(", ")}` : ""}
                  </div>
                )}
                {d.expansion_resuelta?.handled && (
                  <div style={{ color: d.expansion_resuelta.accepted ? "#22c55e" : "#f59e0b", marginBottom: 2 }}>
                    🗺️ Ampliación: {d.expansion_resuelta.accepted ? `aceptada${d.expansion_resuelta.addedValues?.length ? ` · ${d.expansion_resuelta.addedValues.join(", ")}` : ""}` : "rechazada"}
                  </div>
                )}
                {/* Intención visita */}
                {d.intencion_visita && (
                  <div style={{ color: d.intencion_visita.quiere_visita ? "#22c55e" : textMuted, marginBottom: 2 }}>
                    📅 Visita: {d.intencion_visita.quiere_visita ? `✅ SÍ (${d.intencion_visita.confianza})${d.intencion_visita.fecha_mencionada ? " · " + d.intencion_visita.fecha_mencionada : ""}` : "❌ no"}
                    {d.contexto_visita && <span style={{ color: "#22c55e" }}> · hueco propuesto ✓</span>}
                  </div>
                )}
                {/* Intención continuar / conversión */}
                {d.intencion_continuar && (
                  <div style={{ color: d.intencion_continuar.quiere_continuar ? "#a78bfa" : textMuted, marginBottom: 2 }}>
                    👤 Continuar: {d.intencion_continuar.quiere_continuar ? `✅ SÍ (${d.intencion_continuar.confianza}) — ${d.intencion_continuar.motivo}` : `❌ no — ${d.intencion_continuar.motivo}`}
                  </div>
                )}
                {d.conversion && (
                  <div style={{ color: d.conversion.error ? "#ef4444" : d.conversion.ya_existe ? "#f59e0b" : d.conversion.pendiente ? "#f59e0b" : "#22c55e" }}>
                    🔄 Conversión: {d.conversion.error ? `❌ ERROR: ${d.conversion.error}` : d.conversion.ya_existe ? "⚠️ cliente ya existía" : d.conversion.pendiente ? `⏳ pendiente — ${d.conversion.motivo}` : `✅ cliente creado · demanda creada · agente asignado`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Controles */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Grupo ANA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ fontSize: 9, color: textMuted, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Ana</div>
            <div style={{ display: "flex", gap: 5 }}>
              {/* Voz → activa P2 (Ana habla+escribe) */}
              {puedeVerVoz && (
                <CtrlBtn activo={vozActiva} color="green" title={vozActiva ? "Desactivar voz — ir a solo texto" : "Activar voz — ver a Ana"}
                  onClick={() => {
                    const nuevo = !vozActiva;
                    setVozActiva(nuevo);
                    // Al activar voz → P2; al desactivar → P1 (salvo que haya propiedades)
                    if (!panelInmersivo) setPlantillaForzada(nuevo ? "P2" : "P1");
                  }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {vozActiva ? <><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></> : <><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></>}
                  </svg>
                </CtrlBtn>
              )}
              {/* Texto → si voz activa, alterna P2/P4; si voz desactivada → P1 */}
              {puedeVerTexto && (
                <CtrlBtn activo={textoActivo} color="green" title={textoActivo ? "Desactivar texto — Ana pantalla completa" : "Activar texto — ver conversación"}
                  onClick={() => {
                    const nuevo = !textoActivo;
                    setTextoActivo(nuevo);
                    if (!panelInmersivo) {
                      if (vozActiva) {
                        // Con voz: alternar entre P2 (texto+voz) y P4 (solo voz)
                        setPlantillaForzada(nuevo ? "P2" : "P4");
                      } else {
                        // Sin voz: texto activo → P1
                        if (nuevo) setPlantillaForzada("P1");
                      }
                    }
                  }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </CtrlBtn>
              )}
              {/* Vídeo — próximamente, decorativo */}
              <CtrlBtn activo={false} color="green" title="Vídeo — Próximamente" onClick={() => {}}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                  <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </CtrlBtn>
            </div>
          </div>

          <div style={{ width: 1, height: 40, background: border }} />

          {/* Grupo CLIENTE */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ fontSize: 9, color: textMuted, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Cliente</div>
            <div style={{ display: "flex", gap: 5 }}>
              {puedeVerMicrofono && (
                <CtrlBtn activo={microClienteActivo} color="orange" title={microClienteActivo ? "Parar micrófono" : "Activar micrófono"} onClick={iniciarMicrofono}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                  {transcribiendo && <div style={{ position: "absolute", top: -3, right: -3, width: 9, height: 9, borderRadius: "50%", background: "#ef4444", animation: "pulse 1s infinite" }} />}
                </CtrlBtn>
              )}
              {puedeVerTeclado && (
                <CtrlBtn activo={textoClienteActivo} color="orange" title={textoClienteActivo ? "Ocultar teclado" : "Mostrar teclado"}
                  onClick={() => setTextoClienteActivo(v => !v)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" ry="2"/>
                    <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/>
                  </svg>
                </CtrlBtn>
              )}
            </div>
          </div>

          <div style={{ width: 1, height: 40, background: border }} />

          {/* Nueva sesión */}
          {puedeVerNuevaSesion && (
            <button onClick={nuevaSesion} title="Nueva sesión" style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${border}`, background: "transparent", color: textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.52"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          CUERPO CENTRAL — plantillas con transición
          ANA es un elemento flotante único que viaja entre modos
      ══════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0, position: "relative" }}>

        {/* ═══ ANA — imagen única con clip-path animado ═══
            La imagen siempre ocupa toda la pantalla (left:0 top:0 100% x 100%).
            El clip-path recorta cuánto se ve:
              P1: recortada a 0% ancho → invisible (clip al borde izquierdo)
              P2: recortada al 45% izquierdo → columna izquierda
              P4: sin recorte → pantalla completa
              P3: invisible (la imagen mini lo sustituye)
            CSS interpola clip-path suavemente → movimiento real sin salto.
            objectPosition fija: la cara siempre en el mismo punto del lienzo.
        ══════════════════════════════════════════════════════════════ */}
        {/* ═══ ANA — DOS IMÁGENES, cada una en su posición correcta ═══
            P2: izquierda 45%, objectPosition ajustado para centrar la cara
            P4: pantalla completa, objectPosition centrado
            Animan con opacity + scale para dar sensación de expansión
        ══════════════════════════════════════════════════════════════ */}

        {/* — P2: Ana izquierda 45% — */}
        <img src={AnaImg} alt="Ana" style={{
          position: "absolute", left: 0, top: 0,
          width: "45%", height: "100%",
          objectFit: "cover", objectPosition: "center top",
          zIndex: 10,
          opacity: modo === "P2" ? 1 : 0,
          transform: modo === "P2" ? "scale(1)" : "scale(1.03)",
          transformOrigin: "left center",
          transition: "opacity 2.5s cubic-bezier(0.4,0,0.2,1), transform 2.5s cubic-bezier(0.4,0,0.2,1)",
          pointerEvents: "none",
        }} />

        {/* — P4: Ana pantalla completa — */}
        <img src={AnaImg} alt="Ana" style={{
          position: "absolute", left: 0, top: 0,
          width: "100%", height: "100%",
          objectFit: "cover", objectPosition: "center 5%",
          zIndex: 10,
          opacity: modo === "P4" ? 1 : 0,
          transform: modo === "P4" ? "scale(1)" : "scale(0.97)",
          transformOrigin: "left center",
          transition: "opacity 2.5s cubic-bezier(0.4,0,0.2,1), transform 2.5s cubic-bezier(0.4,0,0.2,1)",
          pointerEvents: "none",
        }} />

        {/* — P3: Ana mini circular, esquina inferior izquierda — */}
        <img src={AnaImg} alt="Ana" style={{
          position: "absolute", left: 16, bottom: 30,
          width: 60, height: 60,
          objectFit: "cover", objectPosition: "center 15%",
          borderRadius: "50%",
          border: `2px solid ${anaHablando ? "#22c55e" : "#2563eb"}`,
          boxShadow: anaHablando ? "0 0 16px rgba(34,197,94,0.5)" : "0 0 16px rgba(37,99,235,0.4)",
          zIndex: 20,
          opacity: modo === "P3" ? 1 : 0,
          transform: modo === "P3" ? "scale(1)" : "scale(0.6)",
          transition: "opacity 2s cubic-bezier(0.4,0,0.2,1), transform 2s cubic-bezier(0.4,0,0.2,1), border-color 0.3s, box-shadow 0.3s",
          pointerEvents: "none",
        }} />

        {/* — Badge estado P2 — */}
        <div style={{
          position: "absolute", bottom: 24, left: "22.5%",
          transform: "translateX(-50%)",
          zIndex: 11, pointerEvents: "none",
          opacity: modo === "P2" ? 1 : 0,
          transition: "opacity 2.5s cubic-bezier(0.4,0,0.2,1)",
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "5px 18px", borderRadius: 20,
          fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
          color: anaHablando ? "#22c55e" : "rgba(255,255,255,0.6)",
          display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: anaHablando ? "#22c55e" : "rgba(255,255,255,0.4)", animation: anaHablando ? "pulse 1s infinite" : "none", transition: "background 0.3s" }} />
          {anaHablando ? "Hablando..." : "En línea"}
        </div>

        {/* — Badge estado P4 — */}
        <div style={{
          position: "absolute", bottom: 24, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 11, pointerEvents: "none",
          opacity: modo === "P4" ? 1 : 0,
          transition: "opacity 2.5s cubic-bezier(0.4,0,0.2,1)",
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "5px 18px", borderRadius: 20,
          fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
          color: anaHablando ? "#22c55e" : "rgba(255,255,255,0.6)",
          display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: anaHablando ? "#22c55e" : "rgba(255,255,255,0.4)", animation: anaHablando ? "pulse 1s infinite" : "none", transition: "background 0.3s" }} />
          {anaHablando ? "Hablando..." : "En línea"}
        </div>

        {/* ─── PLANTILLA 1: Solo chat (P1) ─────────────────
            Chat centrado en tercio central, laterales vacíos
        ─────────────────────────────────────────────────── */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex",
          opacity: modo === "P1" ? 1 : 0,
          pointerEvents: modo === "P1" ? "auto" : "none",
          transition: "opacity 2s cubic-bezier(0.4,0,0.2,1)",
          background: bgPage,
        }}>
          <div style={{ flex: 1 }} />
          <div style={{ width: "33%", borderLeft: `1px solid ${border}`, borderRight: `1px solid ${border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div ref={modo === "P1" ? chatRef : null} style={{ flex: 1, overflowY: "auto", padding: "16px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
              {mensajes.map(msg => <Burbuja key={msg.id} msg={msg} isDark={isDark} />)}
              {cargando && (
                <div style={{ alignSelf: "flex-start", display: "flex", gap: 5, padding: "10px 14px", borderRadius: "4px 14px 14px 14px", background: isDark ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)" }}>
                  {[0,1,2].map(i => <span key={i} className="dot-bounce" style={{ width: 7, height: 7, borderRadius: "50%", background: "#2563eb", display: "inline-block" }} />)}
                </div>
              )}
            </div>
          </div>
          <div style={{ flex: 1 }} />
        </div>

        {/* ─── PLANTILLA 2: Columnas cliente + conversación (P2) ───
            El hueco izquierdo (45%) lo ocupa la imagen flotante de Ana
        ─────────────────────────────────────────────────────────── */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex",
          opacity: modo === "P2" ? 1 : 0,
          pointerEvents: modo === "P2" ? "auto" : "none",
          transition: "opacity 2s cubic-bezier(0.4,0,0.2,1)",
          background: "transparent",
        }}>
          {/* Hueco para Ana flotante */}
          <div style={{ width: "45%", flexShrink: 0 }} />

          {/* Cliente 22% */}
          <div style={{ width: "22%", borderLeft: `1px solid ${border}`, borderRight: `1px solid ${border}`, display: "flex", flexDirection: "column", overflow: "hidden", flexShrink: 0, background: bgPage }}>
            <div style={{ padding: "10px 14px", borderBottom: `1px solid ${border}`, background: bgPanel, flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#f59e0b" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: textMuted, letterSpacing: 1, textTransform: "uppercase" }}>Cliente</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              {mensajes.filter(m => m.rol === "usuario").map(msg => (
                <div key={msg.id} style={{ alignSelf: "flex-end", maxWidth: "90%", padding: "9px 12px", borderRadius: "14px 4px 14px 14px", background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)", border: `1px solid ${border}` }}>
                  <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.5 }}>{msg.texto}</div>
                  <div style={{ fontSize: 10, color: textMuted, marginTop: 3, textAlign: "right" }}>{msg.hora}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Conversación Ana 33% */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: bgPage }}>
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${border}`, background: bgPanel, flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: anaHablando ? "#22c55e" : border, transition: "background 0.3s", animation: anaHablando ? "pulse 1s infinite" : "none" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Subtítulos en tiempo real</span>
            </div>
            <div ref={modo === "P2" ? chatRef : null} style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {mensajes.filter(m => m.rol === "ana").map(m => (
                <div key={m.id} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${border}` }}>
                  <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>{m.hora}</div>
                  <div style={{ fontSize: 15, color: textPrimary, lineHeight: 1.8 }}>{m.texto}</div>
                </div>
              ))}
              {anaHablando && subtituloActual && (
                <div>
                  <div style={{ fontSize: 11, color: "#22c55e", marginBottom: 6 }}>● Hablando...</div>
                  <div style={{ fontSize: 15, color: textPrimary, lineHeight: 1.8, opacity: 0.75 }}>
                    {subtituloActual}<span className="blink-cursor">▌</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── PLANTILLA 3: Fotos + chat unificado (P3) ────
            Fotos 16:9 + datos (67%) | Chat WhatsApp (33%)
            La imagen flotante de Ana aparece como avatar mini en la franja datos
        ─────────────────────────────────────────────────── */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex",
          opacity: modo === "P3" ? 1 : 0,
          pointerEvents: modo === "P3" ? "auto" : "none",
          transition: "opacity 2s cubic-bezier(0.4,0,0.2,1)",
          background: bgPage,
        }}>
          {/* Izquierda 67%: fotos + datos */}
          <div style={{ width: "67%", flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${border}`, overflow: "hidden" }}>
            {/* Fotos 16:9 — ocupa el espacio disponible menos la franja de datos */}
            <div style={{ flex: 1, position: "relative", overflow: "hidden", opacity: transicionando ? 0 : 1, transition: "opacity 0.5s ease" }}>
              <CarruselFotos fotos={fotosActivas} isDark={isDark} autoplay={panelInmersivo} />
              {/* Botón cerrar */}
              <button onClick={cerrarPanelInmersivo} style={{
                position: "absolute", top: 14, left: 14, zIndex: 20,
                width: 34, height: 34, borderRadius: 10,
                border: `1px solid ${border}`,
                background: isDark ? "rgba(13,17,23,0.85)" : "rgba(255,255,255,0.9)",
                backdropFilter: "blur(8px)", color: textPrimary,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 700,
              }}>✕</button>
              {/* Ref de la propiedad */}
              {propiedadActiva?.referencia && (
                <div style={{ position: "absolute", top: 14, right: 14, zIndex: 20, background: "rgba(37,99,235,0.85)", backdropFilter: "blur(4px)", padding: "3px 10px", borderRadius: 20, fontSize: 11, color: "white", fontWeight: 700, letterSpacing: 1 }}>
                  {propiedadActiva.referencia}
                </div>
              )}
            </div>

            {/* Franja datos — altura fija */}
            <div style={{ height: 120, flexShrink: 0 }}>
              <FranjaDatos
                propiedad={propiedadActiva}
                isDark={isDark} border={border}
                textPrimary={textPrimary} textMuted={textMuted}
                anaHablando={anaHablando}
                onCerrar={cerrarPanelInmersivo}
              />
            </div>
          </div>

          {/* Derecha 33%: chat unificado WhatsApp */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${border}`, background: bgPanel, flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: anaHablando ? "#22c55e" : border, transition: "background 0.3s", animation: anaHablando ? "pulse 1s infinite" : "none" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Conversación</span>
            </div>
            <div ref={modo === "P3" ? chatRef : null} style={{ flex: 1, overflowY: "auto", padding: "14px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
              {mensajes.map(msg => <Burbuja key={msg.id} msg={msg} isDark={isDark} />)}
              {cargando && (
                <div style={{ alignSelf: "flex-start", display: "flex", gap: 5, padding: "10px 14px", borderRadius: "4px 14px 14px 14px", background: isDark ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)" }}>
                  {[0,1,2].map(i => <span key={i} className="dot-bounce" style={{ width: 7, height: 7, borderRadius: "50%", background: "#2563eb", display: "inline-block" }} />)}
                </div>
              )}
              {anaHablando && subtituloActual && (
                <div style={{ alignSelf: "flex-start", maxWidth: "85%", padding: "9px 12px", borderRadius: "4px 14px 14px 14px", background: isDark ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)" }}>
                  <div style={{ fontSize: 10, color: "#22c55e", marginBottom: 3, fontWeight: 600 }}>● Hablando...</div>
                  <div style={{ fontSize: 13, color: isDark ? "#93c5fd" : "#1d4ed8", lineHeight: 1.5, opacity: 0.85 }}>
                    {subtituloActual}<span className="blink-cursor">▌</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════
          PIE — input + chips
      ══════════════════════════════════════════════════ */}
      <div style={{
        flexShrink: 0, background: bgPanel, borderTop: `1px solid ${border}`,
        padding: "8px 20px", display: "flex", flexDirection: "column", gap: 6,
      }}>
        {/* Banner escuchando */}
        {transcribiendo && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 8, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 2 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1s infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>Escuchando... habla ahora</span>
          </div>
        )}
        {/* Chips */}
        {puedeVerPrompts && (
          <div style={{ display: "flex", gap: 8 }}>
            {["Buscar propiedades", "Ver clientes", "Tareas pendientes", "Resumen del día"].map(chip => (
              <button key={chip} onClick={() => { setInputTexto(chip); inputRef.current?.focus(); }} style={{ padding: "4px 12px", borderRadius: 20, border: `1px solid ${border}`, background: "transparent", color: textMuted, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }}>
                {chip}
              </button>
            ))}
          </div>
        )}
        {/* Input */}
        {puedeVerTeclado && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <textarea
              ref={inputRef}
              value={inputTexto}
              onChange={e => setInputTexto(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={transcribiendo ? "Transcribiendo voz..." : "Escribe un mensaje a Ana... (Enter para enviar)"}
              rows={1}
              style={{
                flex: 1, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                border: `1px solid ${border}`, borderRadius: 8,
                padding: "8px 14px", fontSize: 13, color: textPrimary,
                outline: "none", resize: "none", fontFamily: "inherit",
                height: 36, lineHeight: "20px",
              }}
            />
            <button onClick={() => enviarMensaje()} disabled={cargando || !inputTexto.trim()} style={{
              width: 36, height: 36, borderRadius: 8, border: "none",
              background: cargando || !inputTexto.trim() ? (isDark ? "rgba(37,99,235,0.3)" : "rgba(37,99,235,0.2)") : "#2563eb",
              color: "white", cursor: cargando || !inputTexto.trim() ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              transition: "background 0.2s",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        )}
        {puedeVerTeclado && (
          <div style={{ fontSize: 10, color: textMuted, textAlign: "center" }}>Escribe Enter para enviar · Shift+Enter para nueva línea</div>
        )}
      </div>
    </div>
  );
}
